import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigationStore } from "src/store/navigationStore";
import { useCrimeSceneStore } from "src/store/crimeSceneStore";
import { useAudioStore } from "@/store/audioStore";

interface SimSliderProps {
  min?: number;
  max?: number;
  step?: number;
  className?: string;
}

export default function SimSlider({
  min = 0,
  max = 1,
  step = 0.001,
  className = "",
}: SimSliderProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const progress = useCrimeSceneStore((state) => state.progress);
  const setProgress = useCrimeSceneStore((s) => s.setProgress);

  const isInterrupt = useNavigationStore((s) => s.isInterrupt);
  const setInterrupt = useNavigationStore((s) => s.setInterrupt);
  const setChatAnswer = useNavigationStore((s) => s.setChatAnswer);
  const setPause = useAudioStore((s) => s.setPause);
  const setResume = useAudioStore((s) => s.setResume);

  const simulationCheckpoint = useCrimeSceneStore(
    (state) => state.simulationCheckpoint
  );

  const startAutoPlayDelay = useCrimeSceneStore(
    (state) => state.startAutoPlayDelay
  );
  const stopAutoPlay = useCrimeSceneStore((state) => state.stopAutoPlay);

  const clampValue = (v: number) => Math.min(max, Math.max(min, v));
  const percent = useMemo(() => {
    if (max <= min) return 0;
    return ((progress - min) / (max - min)) * 100;
  }, [progress, min, max]);

  const quantize = (v: number) => {
    const steps = Math.round((v - min) / step);
    return clampValue(min + steps * step);
  };

  const handleValueChange = useCallback(
    (newValue: number) => {
      setProgress(newValue);
      startAutoPlayDelay(2000);
    },
    [setProgress]
  );

  useEffect(() => {
    const handleMove = (clientX: number) => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.min(rect.width, Math.max(0, clientX - rect.left));
      const ratio = rect.width > 0 ? x / rect.width : 0;
      const raw = min + ratio * (max - min);
      handleValueChange(quantize(raw));
    };

    const onMouseMove = (e: MouseEvent) => {
      //console.log("onMouseMove: isDragging=", isDragging);
      setPause();
      if (!isDragging) return;
      handleMove(e.clientX);
    };

    const onTouchMove = (e: TouchEvent) => {
      console.log("onTouchMove: isDragging=", isDragging);
      setPause();
      if (!isDragging) return;
      if (e.touches.length > 0) handleMove(e.touches[0].clientX);
    };

    const stop = () => {
      setIsDragging(false);
      console.log("stop");
      setResume();
    };

    if (isDragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", stop);
      window.addEventListener("touchmove", onTouchMove as any, {
        passive: false,
      });
      window.addEventListener("touchend", stop);
    }

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", onTouchMove as any);
      window.removeEventListener("touchend", stop);

      console.log("sim#isInterrupt=", isInterrupt);

      console.log("sim#nav slider cleanup end");
    };
  }, [isDragging, min, max, step, handleValueChange]);

  const startDragMouse = (e: React.MouseEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };
  const startDragTouch = (e: React.TouchEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };
  const jumpTo = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el) return;
    setChatAnswer("");
    setInterrupt(true);
    const rect = el.getBoundingClientRect();
    const clickX = Math.min(rect.width, Math.max(0, e.clientX - rect.left));
    const ratio = rect.width > 0 ? clickX / rect.width : 0;
    const raw = min + ratio * (max - min);
    handleValueChange(quantize(raw));
    setInterrupt(false);
  };

  return (
    <>
      <div className={`flex w-full select-none ${className} px-[12px]`}>
        <div
          ref={trackRef}
          className="relative h-4 w-full rounded-full flex slider-bg"
          onMouseDown={jumpTo}
          onClick={jumpTo}
        >
          <div
            className="absolute left-0 top-0 h-[16px] rounded-full slider-filled"
            style={{ width: `${percent}%` }}
          />
          {/* thumb */}
          <div
            className="slider-thumb absolute -top-3 h-9 w-9 -translate-x-1/2 cursor-pointer"
            style={{ left: `${percent}%` }}
            onMouseDown={startDragMouse}
            onTouchStart={startDragTouch}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={progress}
            aria-label="Progress"
          >
            <div className="flex h-10 w-10 items-center justify-center slider-filled rounded-full backdrop-blur" />
          </div>
        </div>
      </div>
    </>
  );
}
